import React, { useEffect, useRef, useState } from 'react';
import '../styles/ShareComps.css';
import '../styles/RunComps.css';
import { AllCompsMap, Header, MapLegend, PropertyCard, Switch, SelectionChip, Button, Modal, CompsOverlay, Loading, MobileSelectedComps, IconButton } from '../components';
import { checkOnAuthStateChanged, cloudFunctionV2, getSubDocument, propertyGatewayString, recordEvent, regenerateTimeline } from '../functions';
import { useParams, useNavigate } from 'react-router-dom';
import { SelectedComps, PropertyCardDetail, ImageGallery, ScrollableImages } from '../components';
import { Arrow, House, MapBubble } from '../assets/icons';
import { colour } from '../styles/GlobalStyles';
import { useJsApiLoader } from '@react-google-maps/api';
import { Location04Icon, Settings01Icon } from 'hugeicons-react';
import { ARVEstimateDescription, ARVEstimateHeading, MobileSettings } from '../modalbody';

const libraries = process.env.REACT_APP_GOOGLE_LIBRARIES.split(',');

function ShareComps() {
    const [loggedIn, setLoggedIn] = useState(null);
    const [property, setProperty] = useState(null);
    const [allComps, setAllComps] = useState([]);
    const [filteredComps, setFilteredComps] = useState([]);
    const [selectedComps, setSelectedComps] = useState([]);
    const [expanded, setExpanded] = useState(null);
    const [detailedView, setDetailedView] = useState(null);
    const [selectedPanel, setSelectedPanel] = useState("all");
    const [compTypeDropdownOpen, setCompTypeDropdownOpen] = useState(null);
    const [viewImageGallery, setViewImageGallery] = useState(false);
    const [imageViewProperty, setImageViewProperty] = useState(null);
    const [imageViewPropertyIndex, setImageViewPropertyIndex] = useState(0);
    const [viewScrollableImages, setViewScrollableImages] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [hoverZPID, setHoverZPID] = useState(null);
    const [clickedZPID, setClickedZPID] = useState(null);
    const [loading, setLoading] = useState(true);
    const [neighbourhoodData, setNeighbourhoodData] = useState(null);
    const [tracts, setTracts] = useState([]);
    const [neighbourhoodBounds, setNeighbourhoodBounds] = useState([]);
    const [hoverMapLegend, setHoverMapLegend] = useState(null);
    const [showHeatmap, setShowHeatmap] = useState(false);
    const [selectedCompType, setSelectedCompType] = useState("all");
    const [innerWidth, setInnerWidth] = useState(window.innerWidth);
    const [mapType, setMapType] = useState("roadmap");
    const [rawMap, setRawMap] = useState(null);
    const [compsOverlayExpanded, setCompsOverlayExpanded] = useState(false);
    const [subjectPropertyHover, setSubjectPropertyHover] = useState(false);
    const [arvEstimate, setArvEstimate] = useState(null);
    const [arvRange, setArvRange] = useState(null);
    const [arvDescription, setArvDescription] = useState(null);
    const [asIsEstimate, setAsIsEstimate] = useState(null);
    const [asIsDescription, setAsIsDescription] = useState(null);
    const [mobileSettings, setMobileSettings] = useState(false);
    const [arvDescriptionModal, setArvDescriptionModal] = useState(false);
    const [arvMessages, setArvMessages] = useState([]);
    const [typeOutText, setTypeOutText] = useState(true);
    const [conditionUpdate, setConditionUpdate] = useState([]);
    const fullSelectedComps = allComps.filter(e => selectedComps.findIndex(x => x.zpid === e.uniqueIds.zillowId) !== -1);
    const clickedComp = clickedZPID === null ? null : allComps[allComps.findIndex(e => e.uniqueIds.zillowId === clickedZPID)];
    const navigate = useNavigate();
    const params = useParams();
    const companyId = params === null ? null : params.companyId;
    const countyId = params === null ? null : params.countyId;
    const propertyId = params === null ? null : params.id;
    const compsRef = useRef(null);
    const compsRefMobile = useRef(null);
    const mobileSelectedCompsBody = useRef(null);
    const readOnly = true;
    const bodyRef = useRef(null);
    const darkBgActive = (detailedView !== null || arvDescriptionModal === true || (innerWidth < 1000 && (mobileSettings === true)))

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: JSON.parse(process.env.REACT_APP_GOOGLE).key,
        libraries: libraries
    });

    const compTypeOptions = [
        {
            label: "All",
            value: "all",
            selected: selectedCompType === "all" ? true : false
        },
        {
            label: "ARV",
            value: "arv",
            selected: selectedCompType === "arv" ? true : false
        },
        {
            label: "As-is",
            value: "as-is",
            selected: selectedCompType === "as-is" ? true : false
        },
        {
            label: "Novation",
            value: "novation",
            selected: selectedCompType === "novation" ? true : false
        }
    ];

    const compTypeLegend = [
        {
            label: "ARV comps",
            value: "arv",
            fill: colour.jade01,
            stroke: colour.jade02,
            house: colour.jade03,
            comps: selectedCompType !== "all" && selectedCompType !== "arv" ? [] : allComps.filter(item => selectedComps.findIndex(e => e.zpid === item.uniqueIds.zillowId && e.type === "arv") !== -1)
        },
        {
            label: "As-is comps",
            value: "as-is",
            fill: colour.blue01,
            stroke: colour.blue02,
            house: colour.blue03,
            comps: selectedCompType !== "all" && selectedCompType !== "as-is" ? [] : allComps.filter(item => selectedComps.findIndex(e => e.zpid === item.uniqueIds.zillowId && e.type === "as-is") !== -1)
        },
        {
            label: "Novation comps",
            value: "novation",
            fill: colour.pink01,
            stroke: colour.pink02,
            house: colour.pink03,
            comps: selectedCompType !== "all" && selectedCompType !== "novation" ? [] : allComps.filter(item => selectedComps.findIndex(e => e.zpid === item.uniqueIds.zillowId && e.type === "novation") !== -1)
        },
        {
            label: "Unspecified comps",
            value: null,
            fill: colour.gray02,
            stroke: colour.gray03,
            house: colour.gray04,
            comps: selectedCompType !== "all" ? [] : allComps.filter(item => selectedComps.findIndex(e => e.zpid === item.uniqueIds.zillowId && e.type === null) !== -1)
        }
    ];

    useEffect(() => {
        
        const fetchUserData = async () => {
            const user = await checkOnAuthStateChanged();
            if ( user.status === 200 ) {
                setLoggedIn(true);
                getCompanyReport(user.userId);
            }
            else {
                setLoggedIn(false);
                getCompanyReport(null);
            }
        };

        const getCompanyReport = async(savedUserId) => {
            const colRef = "companies";
            const docRef = companyId;
            const subColRef = "properties";
            const subDocRef = propertyId;
            const queryData = await getSubDocument(colRef, docRef, subColRef, subDocRef);
            if ( queryData.status === 200 ) {
                const data = queryData.data.data;
                setSelectedComps(data.selectedComps);
                fetchPropertyData(savedUserId, data.selectedComps);

                if ( data.arvData !== undefined ) {
                    if ( data.arvMessages !== undefined ) {
                        setArvMessages(data.arvMessages);
                    }

                    setArvEstimate(data.arvData.arv);
                    setArvDescription(data.arvData.description);
                    setArvRange(data.arvData.arvRange);
                }

                if ( data.conditionUpdate ) {
                    setConditionUpdate(data.conditionUpdate);
                }

                if ( data.asIsData !== undefined ) {
                    const compsList = data.asIsData.comps;
                    let match = true;
                    for (let index = 0; index < data.selectedComps.length; index++) {
                        const element = data.selectedComps[index];
                        const zpid = element.zpid;
                        const zpidIndex = compsList.indexOf(zpid);
                        if ( zpidIndex === -1 ) {
                            match = false;
                        }
                    }
                    for (let index = 0; index < compsList.length; index++) {
                        const zpid = compsList[index];
                        const zpidIndex = data.selectedComps.findIndex(e => e.zpid === zpid);
                        if ( zpidIndex === -1 ) {
                            match = false;
                        }
                    }

                    if ( match === true ) {
                        setAsIsEstimate(data.asIsData.asIs);
                        setAsIsDescription(data.asIsData.description);
                    }
                }
            }
            else {
                navigate("/");
            }
        };

        const fetchPropertyData = async(savedUserId, savedComps) => {
            const gatewayURL = process.env.REACT_APP_AWS_QUERY_URL;
            const resourceId = "propertyDetailV2";
            const propertyParams = {
                propertyId: propertyId,
                userId: savedUserId,
                countyId: countyId
            }
            const getGatewayQueryString = await propertyGatewayString("propertyDetailv2?", propertyParams);
            const getGatewayParams = {
                type: "gateway",
                resourceId: resourceId,
                queryString: getGatewayQueryString
            };
            const getGateway = await cloudFunctionV2(gatewayURL, getGatewayParams);
            if ( getGateway.status === 200 ) {
                const body = getGateway.body;
                const data = body.data;
                document.title = `${data.propertyDetails.addressDetails.streetAddress}, ${data.propertyDetails.addressDetails.city}, ${data.propertyDetails.addressDetails.state} ${data.propertyDetails.addressDetails.postalCode} | Clozers`;
                
                setProperty(data.propertyDetails);

                const salesComparables = data.salesComparables.comps;
                const recentlySold = salesComparables.recentlySold;
                const forSale = salesComparables.forSale;
                const fullList = recentlySold.concat(forSale);

                const amendedFullList = [];
                for (let index = 0; index < fullList.length; index++) {
                    const element = fullList[index].propertyDetails;
                    const zpid = element.uniqueIds.zillowId;
                    const zpidIndex = savedComps.findIndex(e => e.zpid === zpid);
                    if ( zpidIndex !== -1 ) {
                        amendedFullList.push(element);
                    }
                }
                amendedFullList.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
                await regenerateTimeline(amendedFullList);
                setAllComps(amendedFullList);
                setFilteredComps(amendedFullList);
                getNeighbourhoodData();
            }
            setLoading(false);
        }

        const getNeighbourhoodData = async() => {
            const neighbourhoodPath = `geo_output/map_neighborhood/county/${countyId}.json`;
            const params = {
                s3Data: {
                    key: neighbourhoodPath,
                    bucketName: "residentialpropertydata"
                },
                type: "s3"
            }
            const awsURL = process.env.REACT_APP_AWS_QUERY_URL;
            const queryData = await cloudFunctionV2(awsURL, params);
            if ( queryData.status === 200 ) {
                const body = queryData.body;
                setNeighbourhoodData(body);

                const newBounds = [];
                const getTracts = [];
                for (let index = 0; index < body.length; index++) {
                    const element = body[index];
                    getTracts.push(element.region.tractId);
                    const tractPolygon = element.tractPolygon.flat(1);
                    newBounds.push(tractPolygon);
                };
                setTracts(getTracts);
                setNeighbourhoodBounds(newBounds);
            }
        }

        const handleWindowResize = () => {
			setInnerWidth(window.innerWidth);
		};
	  
		window.addEventListener('resize', handleWindowResize);

        if ( params === null ) {
            navigate("/");
        }
        else {
            fetchUserData();
        }
    }, [params, navigate, countyId, propertyId, companyId]);

    const showScrollableImages = (item) => {
        setImageViewProperty(item);
        setViewScrollableImages(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
        recordEvent("view_scrollable_images", {});
    };

    const closeScrollableImages = () => {
        setViewScrollableImages(false);
        recordEvent("close_scrollable_images", {});
    };

    const openImageGallery = (index) => {
        setImageViewPropertyIndex(index);
        setViewImageGallery(true);
        setViewScrollableImages(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        recordEvent("open_image_gallery", {});
    };

    const closeImageGallery = () => {
        setViewImageGallery(false);
        setViewScrollableImages(true);
        setImageViewPropertyIndex(0);
        recordEvent("close_image_gallery", {});
    };

    const openDetailedView = (item) => {
        setDetailedView(item);
        recordEvent("open_detailed_view", {
            zpid: item.uniqueIds.zillowId
        });
    };

    const closeDetailedView = () => {
        setDetailedView(null);
        recordEvent("close_detailed_view", {});
    };

    const onChangeZPIDHover = (id) => {
        setHoverZPID(id);
    };

    const downloadProperties = async() => {
        setDownloadLoading(true);
        const url = process.env.REACT_APP_GENERATE_SPREADSHEET;
        
        const cloneSelectedComps = [...fullSelectedComps];
        for (let index = 0; index < cloneSelectedComps.length; index++) {
            const element = cloneSelectedComps[index];
            const selectedCompIndex = selectedComps.findIndex(e => e.zpid === element.uniqueIds.zillowId);
            if ( selectedCompIndex !== -1 ) {
                element.type = selectedComps[selectedCompIndex].type;
            }
            else {
                element.type = null;
            }    
        }

        const params = {
            userId: companyId,
            countyId: countyId,
            subjectProperty: property,
            comparables: cloneSelectedComps,
            downloadData: false,
            readOnly: true
        };

        const newWindow = window.open("", "_blank");
        const queryData = await cloudFunctionV2(url, params);
        if ( queryData.status === 200 ) {
            if ( newWindow === null ) {
                window.open(queryData.data, "_blank");
                newWindow.close();
            }
            else {
                newWindow.location.href = queryData.data;
            }
        }
        else {
            console.log("Error: ", queryData.error);
        }

        setDownloadLoading(false);
        recordEvent("download_properties", {});
    };

    const closeDropdowns = () => {
        setCompTypeDropdownOpen(null);
    };

    const viewComp = (item) => {
        const id = item.uniqueIds.zillowId;
        setClickedZPID(id)

        if ( compsRef.current !== null ) {
            setTimeout(() => {
                compsRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }, 50);
        }
        recordEvent("view_comp", {
            zpid: id
        });
    };

    const handleCompTypeSelection = (value) => {
        setSelectedCompType(value);

        if ( value === "all" ) {
            setFilteredComps(allComps);
        }
        else {
            const filtered = selectedComps.filter(item => item.type === value);
            const filteredAll = allComps.filter(item => filtered.findIndex(e => e.zpid === item.uniqueIds.zillowId && e.type === value) !== -1);
            setFilteredComps(filteredAll);
        }
        recordEvent("change_comp_type_filter", {
            value: value
        });
    };

    const toggleSettings = () => {
        setMobileSettings(true);
    };

    const closeSettings = () => {
        setMobileSettings(false);
    };

    const toggleMobileCompsList = () => {
        setCompsOverlayExpanded(!compsOverlayExpanded);
    };

    const closeARVDescriptionModal = () => {
        recordEvent("close_arv_description_modal", {});
        setArvDescriptionModal(false);
    };

    return (
        <div className={"share-comps-outer-container " + (darkBgActive === true ? "relative-container" : "")}>
            <Header
                loggedIn={loggedIn}
            />
            {
                viewImageGallery === true && imageViewProperty !== null &&
                <ImageGallery
                    alt={imageViewProperty.addressDetails.streetAddress}
                    images={imageViewProperty.listingDetails.mlsPhotoUrls}
                    imageIndex={imageViewPropertyIndex}
                    setImageIndex={setImageViewPropertyIndex}
                    closeImageGallery={closeImageGallery}
                />
            }
            {
                viewScrollableImages === true && imageViewProperty !== null &&
                <ScrollableImages
                    images={imageViewProperty.listingDetails.mlsPhotoUrls}
                    closeScrollableImages={closeScrollableImages}
                    imageClick={openImageGallery}
                />
            }
            {
                selectedComps.length > 0 && loading === false &&
                <SelectedComps
                    allSelectedComps={selectedComps}
                    setAllSelectedComps={setSelectedComps}
                    fullSelectedComps={fullSelectedComps}
                    toggleSelectedComp={null}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    showScrollableImages={showScrollableImages}
                    openDetailedView={openDetailedView}
                    downloadProperties={downloadProperties}
                    shareLink={null}
                    downloadLoading={downloadLoading}
                    selectedPanel={selectedPanel}
                    setSelectedPanel={setSelectedPanel}
                    compTypeDropdownOpen={compTypeDropdownOpen}
                    setCompTypeDropdownOpen={setCompTypeDropdownOpen}
                    closeDropdowns={closeDropdowns}
                    companyId={companyId}
                    property={property}
                    linkCopied={false}
                    linkCopiedError={false}
                    readOnly={readOnly}
                    compTypeLegend={compTypeLegend}
                    isLoaded={isLoaded}
                    bestComps={[]}
                    innerWidth={innerWidth}
                    getARV={null}
                    arvLoading={false}
                    arvEstimate={arvEstimate}
                    arvDescription={arvDescription}
                    getAsIs={null}
                    asIsEstimate={asIsEstimate}
                    asIsDescription={asIsDescription}
                    onboardingStep={null}
                    nextOnboardingStep={null}
                    mobileSelectedCompsBody={mobileSelectedCompsBody}
                    autoKeepComps={null}
                    setArvDescriptionModal={setArvDescriptionModal}
                    compsChanged={false}
                    setRegenerateARVErrorModal={null}
                    actionRequired={false}
                    setSmallChatExpanded={null}
                    bodyRef={bodyRef}
                />
            }
            {
                selectedComps.length > 0 && loading === false &&
                <MobileSelectedComps
                    allSelectedComps={selectedComps}
                    setAllSelectedComps={setSelectedComps}
                    fullSelectedComps={fullSelectedComps}
                    toggleSelectedComp={null}
                    expanded={expanded}
                    setExpanded={setExpanded}
                    showScrollableImages={showScrollableImages}
                    openDetailedView={openDetailedView}
                    downloadProperties={downloadProperties}
                    shareLink={null}
                    downloadLoading={downloadLoading}
                    selectedPanel={selectedPanel}
                    setSelectedPanel={setSelectedPanel}
                    compTypeDropdownOpen={compTypeDropdownOpen}
                    setCompTypeDropdownOpen={setCompTypeDropdownOpen}
                    closeDropdowns={closeDropdowns}
                    companyId={companyId}
                    property={property}
                    linkCopied={null}
                    linkCopiedError={false}
                    readOnly={readOnly}
                    compTypeLegend={compTypeLegend}
                    isLoaded={isLoaded}
                    bestComps={[]}
                    innerWidth={innerWidth}
                    getARV={null}
                    arvLoading={false}
                    arvEstimate={arvEstimate}
                    arvDescription={arvDescription}
                    getAsIs={null}
                    asIsEstimate={asIsEstimate}
                    asIsDescription={asIsDescription}
                    onboardingStep={null}
                    nextOnboardingStep={null}
                    mobileSelectedCompsBody={mobileSelectedCompsBody}
                />
            }
            {
                detailedView !== null &&
                <PropertyCardDetail
                    property={detailedView}
                    selected={selectedComps.findIndex(e => e.zpid === detailedView.uniqueIds.zillowId) !== -1 ? true : false}
                    closeDetailedView={closeDetailedView}
                    toggleSelectedComp={null}
                    showScrollableImages={showScrollableImages}
                    isLoaded={isLoaded}
                    subjectProperty={property}
                    clickedCompDescription={null}
                    readOnly={readOnly}
                    bestComp={false}
                    loading={loading}
                    conditionUpdate={conditionUpdate}
                />
            }
            {
                darkBgActive === true &&
                <div className="dark-bg">
                </div>
            }
            {
                mobileSettings === true && innerWidth < 1000 &&
                    <Modal
                        heading="Settings"
                        subheading={null}
                        body={<MobileSettings showHeatmap={showHeatmap} setShowHeatmap={setShowHeatmap} mapType={mapType} setMapType={setMapType} neighbourhoodData={neighbourhoodData} rawMap={rawMap} conditionOptions={null} handleConditionSelect={null} compTypeOptions={compTypeOptions} handleCompTypeSelection={handleCompTypeSelection} />}
                        type="none"
                        cancelText={null}
                        cancelButtonCTA={closeSettings}
                        actionButtonText={null}
                        actionButtonCTA={null}
                        secondaryButtonText={null}
                        secondaryButtonCTA={null}
                        exitButtonCTA={closeSettings}
                        loading={false}
                        actionButtonDisabled={false}
                        leftIcon={null}
                        rightIcon={null}
                        cancelLeftIcon={null}
                        cancelRightIcon={null}
                        customClass=""
                    />
            }
            {
                arvDescriptionModal === true &&
                <Modal
                    heading="ARV Estimate reasoning"
                    subheading={null}
                    subheadingComponent={<ARVEstimateHeading arvEstimate={arvEstimate} arvRange={arvRange} />}
                    body={<ARVEstimateDescription arvDescription={arvDescription} arvMessages={arvMessages} loading={false} textAreaHeight={0} compsChanged={false} getARV={null} arvLoading={false} typeOutText={typeOutText} setTypeOutText={setTypeOutText} readOnly={readOnly} scenarios={null} actionRequired={false} />}
                    type={"none"}
                    cancelText={null}
                    cancelButtonCTA={closeARVDescriptionModal}
                    actionButtonText={"Close"}
                    actionButtonCTA={closeARVDescriptionModal}
                    secondaryButtonText={null}
                    secondaryButtonCTA={null}
                    exitButtonCTA={closeARVDescriptionModal}
                    loading={false}
                    actionButtonDisabled={false}
                    leftIcon={null}
                    rightIcon={null}
                    cancelLeftIcon={null}
                    cancelRightIcon={null}
                    customClass="arv-estimate-modal"
                    promptInput={false}
                    prompt={null}
                    onChangePrompt={null}
                    submitPrompt={null}
                    arvMessages={arvMessages}
                    setTextAreaHeight={null}
                />
            }
            {
                loading === true &&
                <Loading />
            }
            {
                property === null ?
                <div className="run-comps-inner-container">
                </div>
                :
                <div className="run-comps-inner-container">
                    <div 
                        className="run-comps-body-container"
                        ref={bodyRef}
                    >
                        <div className="run-comps-header-outer-container">
                            <div className="run-comps-header-left-container">
                                <a 
                                    className="text-button button-row gap-x-small no-underline mobile-none"
                                    href="/"
                                >
                                    <Arrow
                                        className=""
                                        fill={colour.gray06}
                                    />
                                    <span className="body-regular colour-primary">
                                        New search
                                    </span>
                                </a>
                                <div className="run-comps-filters-container mobile-none">
                                    {
                                        compTypeOptions.map((item, index) =>
                                            <SelectionChip
                                                label={item.label}
                                                selected={item.selected}
                                                disabled={false}
                                                func={() => handleCompTypeSelection(item.value)}
                                                index={index}
                                                leftIcon={null}
                                                rightIcon={null}
                                                key={index}
                                            />
                                        )
                                    }
                                    <span className="body-regular colour-primary">
                                        Showing <span className="body-bold">{allComps.length}</span> comps
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="run-comps-mobile-header-container desktop-none">
                            <div className="run-comps-mobile-header-button-container">
                                <Button
                                    buttonType="secondary-button"
                                    size="large"
                                    text={compsOverlayExpanded === true ? "Map view" : "Comps list"}
                                    cta={toggleMobileCompsList}
                                    disabled={false}
                                    leftIcon={compsOverlayExpanded === false ? <House className="" fill={colour.gray06} /> : <Location04Icon size={16} color={colour.gray06} />}
                                    leftIconClass=""
                                    rightIcon={null}
                                    rightIconClass=""
                                    alt=""
                                    standardIcon={false}
                                    badge={false}
                                    badgeVal={null}
                                    badgeBG={null}
                                    buttonRef={null}
                                    spinner={false}
                                />
                            </div>
                            <div className="run-comps-mobile-header-button-container">
                                <IconButton
                                    size="large"
                                    icon={<Settings01Icon color={colour.gray06} size={24} />}
                                    disabled={false}
                                    clickFunc={toggleSettings}
                                    standardIcon={false}
                                    background={true}
                                    bgType={"white"}
                                    contextTooltip={false}
                                    contextTooltipBG={null}
                                    contextTooltipText={null}
                                    customClass={null}
                                />
                            </div>
                        </div>
                        <div className="run-comps-inner-body-container">
                            <div className="run-comps-map-container">
                                <AllCompsMap
                                    subjectProperty={property}
                                    comps={filteredComps}
                                    isMapLoaded={isLoaded}
                                    clickedZPID={clickedZPID}
                                    viewComp={viewComp}
                                    bestComps={[]}
                                    hoverZPID={hoverZPID}
                                    neighbourhoodData={neighbourhoodData}
                                    tracts={tracts}
                                    neighbourhoodBounds={neighbourhoodBounds}
                                    hoverMapLegend={hoverMapLegend}
                                    setHoverMapLegend={setHoverMapLegend}
                                    showHeatmap={showHeatmap}
                                    readOnly={readOnly}
                                    compTypeLegend={compTypeLegend}
                                    innerWidth={innerWidth}
                                    mapType={mapType}
                                    setMapType={setMapType}
                                    rawMap={rawMap}
                                    setRawMap={setRawMap}
                                    setSubjectPropertyHover={setSubjectPropertyHover}
                                    ceilings={[]}
                                    selectedComps={selectedComps}
                                />
                                {
                                    neighbourhoodData !== null && innerWidth >= 1000 &&
                                    <div className={"run-comp-map-bottom-container " + (showHeatmap === false ? "run-comp-hide-heatmap" : "")}>
                                        <MapLegend
                                            hoverMapLegend={hoverMapLegend}
                                            setHoverMapLegend={setHoverMapLegend}
                                            neighbourhoodOption={0}
                                        />
                                        <Switch
                                            label={"Heatmap"}
                                            val={showHeatmap}
                                            setVal={setShowHeatmap}
                                        />
                                    </div>
                                }
                            </div>
                            <div className={"run-comps-subject-property-section mobile-none " + (subjectPropertyHover === true ? "run-comps-subject-property-section-hover" : "")}>
                                <div className="run-comps-subject-property-title-container">
                                    <div className="relative-container">
                                        <MapBubble
                                            className=""
                                            fill={colour.gray07}
                                            stroke={colour.gray00}
                                            strokeWidth="0.5"
                                        />
                                        <House
                                            fill={colour.gray00}
                                            className="all-comps-map-house-icon"
                                        />
                                    </div>
                                    <span className="body-bold colour-primary">
                                        Subject property
                                    </span>
                                </div>
                                <PropertyCard
                                    property={property}
                                    subjectProperty={true}
                                    subjectPropertyObject={property}
                                    showScrollableImages={showScrollableImages}
                                    detail={false}
                                    isLoaded={isLoaded}
                                    testView={false}
                                    alwaysActive={true}
                                    selected={false}
                                    toggleSelectedComp={null}
                                    bestComp={false}
                                    openDetailedView={null}
                                    clickedZPID={clickedZPID}
                                    clickedCompDescription={null}
                                    loading={false}
                                    onChangeZPIDHover={null}
                                    readOnly={readOnly}
                                    innerWidth={innerWidth}
                                    subjectPropertyHover={subjectPropertyHover}
                                    gptLoading={false}
                                    priceCeiling={false}
                                    priceCeilingText={null}
                                    darkMode={false}
                                    conditionUpdate={[]}
                                />
                            </div>
                            <div 
                                className="run-comps-cards-section mobile-none"
                                ref={compsRef}
                            >
                                {
                                    clickedZPID !== null &&
                                    <PropertyCard
                                        property={clickedComp}
                                        subjectProperty={false}
                                        subjectPropertyObject={property}
                                        showScrollableImages={showScrollableImages}
                                        isLoaded={isLoaded}
                                        testView={false}
                                        alwaysActive={true}
                                        selected={null}
                                        toggleSelectedComp={null}
                                        bestComp={false}
                                        openDetailedView={openDetailedView}
                                        clickedZPID={clickedZPID}
                                        clickedCompDescription={null}
                                        loading={loading}
                                        onChangeZPIDHover={null}
                                        readOnly={readOnly}
                                        innerWidth={innerWidth}
                                        subjectPropertyHover={false}
                                        gptLoading={false}
                                        priceCeiling={false}
                                        priceCeilingText={null}
                                        darkMode={false}
                                        conditionUpdate={conditionUpdate}
                                    />
                                }
                                {
                                    compTypeLegend.map((item, index) =>
                                        item.comps.length > 0 &&
                                        <React.Fragment 
                                            key={index}
                                        >
                                            <div className="run-comps-best-comps-title-container">
                                                <div className="relative-container">
                                                    <MapBubble
                                                        className=""
                                                        fill={item.fill}
                                                        stroke={item.stroke}
                                                        strokeWidth="0.5"
                                                    />
                                                    <House
                                                        fill={item.house}
                                                        className="all-comps-map-house-icon"
                                                    />
                                                </div>
                                                <span className="body-bold colour-primary">
                                                    {item.label}
                                                </span>
                                            </div>
                                            {
                                                item.comps.map((comp, ii) =>
                                                    comp.uniqueIds.zillowId !== clickedZPID &&
                                                    <PropertyCard
                                                        property={comp}
                                                        subjectProperty={false}
                                                        subjectPropertyObject={property}
                                                        showScrollableImages={showScrollableImages}
                                                        isLoaded={isLoaded}
                                                        key={ii}
                                                        testView={false}
                                                        alwaysActive={true}
                                                        selected={selectedComps.findIndex(e => e.zpid === comp.uniqueIds.zillowId) !== -1 ? true : false}
                                                        toggleSelectedComp={null}
                                                        bestComp={true}
                                                        openDetailedView={openDetailedView}
                                                        clickedZPID={clickedZPID}
                                                        clickedCompDescription={null}
                                                        loading={false}
                                                        onChangeZPIDHover={onChangeZPIDHover}
                                                        readOnly={readOnly}
                                                        innerWidth={innerWidth}
                                                        subjectPropertyHover={false}
                                                        gptLoading={false}
                                                        priceCeiling={false}
                                                        priceCeilingText={null}
                                                        darkMode={false}
                                                        conditionUpdate={conditionUpdate}
                                                    />
                                                )
                                            }
                                        </React.Fragment>
                                    )
                                }
                            </div>
                        </div>
                         <CompsOverlay
                            expanded={compsOverlayExpanded}
                            property={property}
                            showScrollableImages={showScrollableImages}
                            isLoaded={isLoaded}
                            clickedZPID={clickedZPID}
                            readOnly={readOnly}
                            compsRef={compsRefMobile}
                            clickedComp={clickedComp}
                            bestComps={[]}
                            selectedComps={selectedComps}
                            loading={loading}
                            clickedCompDescription={null}
                            toggleSelectedComp={null}
                            openDetailedView={openDetailedView}
                            onChangeZPIDHover={onChangeZPIDHover}
                            innerWidth={innerWidth}
                            filteredComps={filteredComps}
                            allComps={allComps}
                            compTypeLegend={compTypeLegend}
                            gptLoading={false}
                            ceilings={[]}
                            conditionUpdate={conditionUpdate}
                        />
                    </div>
                </div>
            }
        </div>
    )
};

export default ShareComps;